import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { keyframes } from '@emotion/react';
import { Box, Flex, Grid, GridItem, HStack, Text, Spinner, Icon, Tooltip, type TextProps } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@chakra-ui/icons';
import TeamLogo from './TeamLogo';
import {
  BROADCAST_PBP_BETWEEN_MS,
  BROADCAST_PBP_INNING_POPUP_MULTIPLIER,
  BROADCAST_PBP_LABEL_COLOR_DEFENSIVE_CHANGE,
  BROADCAST_PBP_LABEL_COLOR_GAME_OVER,
  BROADCAST_PBP_LABEL_COLOR_PINCH_HITTER,
  BROADCAST_PBP_LABEL_COLOR_RELIEF_PITCHER,
  BROADCAST_PBP_LABEL_COLOR_OUT,
  BROADCAST_PBP_LABEL_COLOR_ERROR,
  BROADCAST_PBP_POPUP_MS,
} from '../constants';
import {
  BROADCAST_PBP_LABEL_PREFIX,
  formatScoringPlayLine,
  parseBroadcastPbpDisplay,
  stripBroadcastPbpDecoration,
  type BroadcastPbpLabelKind,
} from '../utils/broadcastPbpDisplay';
import { getBroadcastEventTypeLabel } from '../utils/broadcastEventTypeLabels';

const SPONSOR_PRIMARY = 'Mobile Global eSports.';
const SPONSOR_TAGLINE = 'Makers of Reality Sports Online and Dominus Sports.';

const BROADCAST_PBP_LABEL_CHAKRA_COLOR: Record<BroadcastPbpLabelKind, string> = {
  gameOver: BROADCAST_PBP_LABEL_COLOR_GAME_OVER,
  pinch: BROADCAST_PBP_LABEL_COLOR_PINCH_HITTER,
  defensive: BROADCAST_PBP_LABEL_COLOR_DEFENSIVE_CHANGE,
  relief: BROADCAST_PBP_LABEL_COLOR_RELIEF_PITCHER,
};

function getRunScoredTag(playText: string, eventTypeLabel: string): string {
  const matches = playText.match(/\bscores?\s+from\b/gi) ?? [];
  if (matches.length > 1) return 'RUNS SCORE';
  if (matches.length === 1) return 'RUN SCORES';
  if (eventTypeLabel === 'HOME RUN') return 'RUN SCORES';
  return '';
}

function getOutCountTag(outs: number, previousOuts: number): string {
  if (outs <= previousOuts || outs < 1 || outs > 3) return '';
  return outs === 1 ? '1 OUT' : `${outs} OUTS`;
}

function playDescriptionHasError(text: string): boolean {
  return /\berror\b/i.test(text);
}

/** When an error pill is shown, drop trailing “ OUT” from titles like GROUND BALL OUT. */
function stripOutFromPlayTitle(label: string): string {
  return label.replace(/\s+OUT$/i, '').trim();
}

function withTrailingPeriod(text: string): string {
  const trimmed = text.trimEnd();
  if (!trimmed || trimmed.endsWith('.')) return trimmed;
  return `${trimmed}.`;
}

/** Split trailing “Score is H - A” from simulator announcements when runs score. */
function splitScoreFromDescription(text: string): { main: string; scoreText: string | null } {
  const match = text.match(/\.\s*(Score is\s+\d+\s*-\s*\d+)\s*\.?\s*$/i);
  if (!match || match.index == null) return { main: text, scoreText: null };
  return {
    main: text.slice(0, match.index).trimEnd(),
    scoreText: match[1].trim(),
  };
}

function PbpInlinePill({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <>
      {' \u00A0'}
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        px={2}
        py={0.5}
        borderWidth="1px"
        borderColor={color}
        borderRadius="full"
        fontWeight="bold"
        color={color}
        fontSize="xs"
        lineHeight="1"
        verticalAlign="middle"
      >
        {children}
      </Box>
      {'\u00A0 '}
    </>
  );
}

function BroadcastPbpDisplayText({
  raw,
  eventType,
  outs,
  previousOuts = 0,
  outCountPlacement = 'end',
  half,
  awayScore,
  homeScore,
  previousAwayScore = 0,
  previousHomeScore = 0,
  awayTeamName,
  homeTeamName,
  ...textProps
}: {
  raw: string;
  eventType?: string;
  outs?: number;
  previousOuts?: number;
  /** List view: outs after play title; popup: outs at end of description. */
  outCountPlacement?: 'afterTitle' | 'end';
  half?: string;
  awayScore?: number;
  homeScore?: number;
  previousAwayScore?: number;
  previousHomeScore?: number;
  awayTeamName?: string;
  homeTeamName?: string;
} & TextProps) {
  const eventTypeLabel = getBroadcastEventTypeLabel(eventType);
  const isHomeRunPlay = eventTypeLabel === 'HOME RUN';
  const trimmed = (raw ?? '').trim();
  if (!trimmed) {
    const emptyPrefix = eventTypeLabel ? `${eventTypeLabel} | ` : '';
    return (
      <Text {...textProps}>
        {emptyPrefix || '—'}
      </Text>
    );
  }
  const { label, body } = parseBroadcastPbpDisplay(trimmed);
  if (!label) {
    const runScoredTag = getRunScoredTag(body, eventTypeLabel);
    const outCountTag = outs != null ? getOutCountTag(outs, previousOuts) : '';
    const { main: bodyMain, scoreText } = body ? splitScoreFromDescription(body) : { main: '', scoreText: null };
    const hasError = playDescriptionHasError(bodyMain || body);
    const showErrorPill = hasError && outCountPlacement === 'afterTitle';
    const displayEventTypeLabel =
      showErrorPill && eventTypeLabel ? stripOutFromPlayTitle(eventTypeLabel) : eventTypeLabel;
    const titlePillFollows =
      outCountPlacement === 'afterTitle' && Boolean(outCountTag || runScoredTag || hasError);
    const eventTypePrefix = displayEventTypeLabel
      ? titlePillFollows
        ? displayEventTypeLabel
        : `${displayEventTypeLabel} | `
      : '';
    const displayScoreLine =
      scoreText &&
      half != null &&
      awayScore != null &&
      homeScore != null &&
      awayTeamName &&
      homeTeamName
        ? formatScoringPlayLine({
            half,
            awayScore,
            homeScore,
            previousAwayScore,
            previousHomeScore,
            awayTeamName,
            homeTeamName,
          }) ?? scoreText
        : scoreText;
    const displayBody = bodyMain ? withTrailingPeriod(bodyMain) : displayScoreLine ? '' : '—';
    const outCountEl =
      outCountTag && outCountPlacement === 'afterTitle' ? (
        <PbpInlinePill color={BROADCAST_PBP_LABEL_COLOR_OUT}>{outCountTag}</PbpInlinePill>
      ) : null;
    const outCountAtEndEl =
      outCountTag && outCountPlacement === 'end' ? (
        <>
          <br />
          <Text as="span" fontWeight="bold" color={BROADCAST_PBP_LABEL_COLOR_OUT}>
            {outCountTag}
          </Text>
        </>
      ) : null;
    const errorEl =
      showErrorPill ? (
        <PbpInlinePill color={BROADCAST_PBP_LABEL_COLOR_ERROR}>ERROR</PbpInlinePill>
      ) : null;
    const runScoredEl =
      runScoredTag && outCountPlacement === 'afterTitle' ? (
        <PbpInlinePill color={BROADCAST_PBP_LABEL_COLOR_RELIEF_PITCHER}>{runScoredTag}</PbpInlinePill>
      ) : runScoredTag ? (
        <Text as="span" fontWeight="bold" color={BROADCAST_PBP_LABEL_COLOR_RELIEF_PITCHER}>
          {runScoredTag} |{' '}
        </Text>
      ) : null;
    return (
      <Text {...textProps}>
        {eventTypePrefix ? (
          <Text as="span" fontWeight="bold" color={isHomeRunPlay ? BROADCAST_PBP_LABEL_COLOR_RELIEF_PITCHER : 'white'}>
            {eventTypePrefix}
          </Text>
        ) : null}
        {outCountEl}
        {errorEl}
        {runScoredEl}
        {/* {outCountTag ? (
          <Text as="span" fontWeight="bold" color={BROADCAST_PBP_LABEL_COLOR_OUT}>
            {outCountTag} |{' '}
          </Text>
        ) : null} */}
        {displayBody}
        {displayScoreLine ? (
          <>
            {outCountPlacement === 'end' ? <br /> : displayBody ? ' ' : null}
            <Text as="span" fontWeight="bold" color={BROADCAST_PBP_LABEL_COLOR_RELIEF_PITCHER}>
              {displayScoreLine}
            </Text>
          </>
        ) : null}
        {outCountAtEndEl}
      </Text>
    );
  }
  return (
    <Text {...textProps}>
      <Text as="span" fontWeight="bold" color={BROADCAST_PBP_LABEL_CHAKRA_COLOR[label]}>
        {BROADCAST_PBP_LABEL_PREFIX[label]}
      </Text>
      {body ? withTrailingPeriod(body) : body}
    </Text>
  );
}

function extractDueUpText(raw: string): string {
  const { body } = parseBroadcastPbpDisplay(raw ?? '');
  const match = body.match(/Due up for\s+.*?(?:\.|$)/i);
  return (match?.[0] ?? body).trim() || '—';
}

function parseInningStartBody(raw: string): { scoreText: string; dueUpText: string } {
  const trimmed = (raw ?? '').trim();
  const withoutIntro = trimmed.replace(/^It's the (?:Top|Bottom) of inning \d+\.\s*/i, '');
  const dueUpMatch = withoutIntro.match(/Due up for\s+.+/i);
  const dueUpText = dueUpMatch ? dueUpMatch[0].replace(/\s+/g, ' ').trim() : '';
  let scoreText = withoutIntro;
  if (dueUpMatch && dueUpMatch.index != null) {
    scoreText = withoutIntro.slice(0, dueUpMatch.index).trim();
  }
  scoreText = scoreText.replace(/\.\s*$/, '').trim();
  return { scoreText, dueUpText };
}

function parseGameStartBody(raw: string): { headline: string; detailText: string } {
  const cleaned = stripBroadcastPbpDecoration(raw)
    .replace(/^Here we go!\s*/i, '')
    .trim();
  const match = cleaned.match(/^It's\s+(.+?)\s+at\s+(.+?)\.\s*(.*)$/is);
  if (!match) {
    return { headline: 'Play ball!', detailText: cleaned };
  }
  const awayTeam = match[1].trim();
  const homeTeam = match[2].trim();
  const detailText = match[3].replace(/\s+/g, ' ').trim();
  return {
    headline: `Play ball! It's ${awayTeam} at ${homeTeam}.`,
    detailText,
  };
}

function InningStartPopupText({ row, ...textProps }: { row: PlayByPlayParsedRow } & TextProps) {
  const isGameStart = row.event_type.toLowerCase() === 'game start';

  if (isGameStart) {
    const { headline, detailText } = parseGameStartBody(row.text);
    return (
      <Text {...textProps}>
        <Text as="span" fontWeight="bold">
          {headline}
        </Text>
        {detailText ? (
          <>
            <br />
            {withTrailingPeriod(detailText)}
          </>
        ) : null}
      </Text>
    );
  }

  const half = row.half === 'top' ? 'top' : 'bottom';
  const inningHeading = `It's the ${half} of the ${ordinal(row.inning || 1)} inning.`;
  const { scoreText, dueUpText } = parseInningStartBody(row.text);

  return (
    <Text {...textProps}>
      <Text as="span" fontWeight="bold">
        {inningHeading}
      </Text>
      {scoreText ? <> {withTrailingPeriod(scoreText)}</> : null}
      {dueUpText ? (
        <>
          <br />
          {dueUpText}
        </>
      ) : null}
    </Text>
  );
}

function InningStartFeedCard({
  row,
  awayTeam,
  homeTeam,
}: {
  row: PlayByPlayParsedRow;
  awayTeam: TeamPanel;
  homeTeam: TeamPanel;
}) {
  const isTop = row.half === 'top';
  const heading = `${isTop ? 'TOP' : 'BOTTOM'} ${row.inning || 1}`;
  const dueUp = extractDueUpText(row.text);

  return (
    <Box
      px={2}
      pt={3}
      pb={2}
      borderRadius="md"
      bgGradient="linear(to-b, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 42%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.02) 60%, rgba(0,0,0,0.00) 100%)"
      borderWidth="1px"
      // borderColor="rgba(255,255,255,0.10)"
      // boxShadow="0 6px 24px rgba(0, 0, 0, 0.28)"
    >
      <Grid templateColumns="auto minmax(0,1fr)" gap={3} alignItems="start">
        <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color="gray.100" lineHeight="1">
          {heading}
        </Text>
        <Box w="100%" display="flex" justifyContent="flex-end" minW={0}>
          <Box as="table" w="auto" sx={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
            <Box as="tbody">
              <Box as="tr">
                <Box as="td" w="11px" pr={2}>
                  <Box w="8px" h="8px" borderRadius="full" bg={isTop ? 'orange.400' : 'transparent'} mx="auto" />
                </Box>
                <Box as="td" w="44px" pr={3} pb={2}>
                  <TeamLogo
                    team={{
                      name: awayTeam.name,
                      logo: awayTeam.logo,
                      color_primary: awayTeam.color_primary,
                      color_secondary: awayTeam.color_secondary,
                      color_text: awayTeam.color_text,
                    }}
                    size="sm"
                  />
                </Box>
                <Box as="td" pr={2} w="112px">
                  <Text fontSize="md" color="gray.100" fontWeight="semibold" isTruncated>
                    {awayTeam.name}
                  </Text>
                </Box>
                <Box as="td" w="32px">
                  <Text color="gray.100" fontWeight="bold" fontSize="lg" textAlign="right">
                    {row.away_score}
                  </Text>
                </Box>
              </Box>
              <Box as="tr">
                <Box as="td" w="11px" pr={2}>
                  <Box w="8px" h="8px" borderRadius="full" bg={!isTop ? 'orange.400' : 'transparent'} mx="auto" />
                </Box>
                <Box as="td" w="44px" pr={3}>
                  <TeamLogo
                    team={{
                      name: homeTeam.name,
                      logo: homeTeam.logo,
                      color_primary: homeTeam.color_primary,
                      color_secondary: homeTeam.color_secondary,
                      color_text: homeTeam.color_text,
                    }}
                    size="sm"
                  />
                </Box>
                <Box as="td" pr={2} w="112px">
                  <Text fontSize="md" color="gray.100" fontWeight="semibold" isTruncated>
                    {homeTeam.name}
                  </Text>
                </Box>
                <Box as="td" w="32px">
                  <Text color="gray.100" fontWeight="bold" fontSize="lg" textAlign="right">
                    {row.home_score}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>
      <Text mt={2} fontSize="sm" color="whiteAlpha.800" lineHeight="short">
        {dueUp}
      </Text>
    </Box>
  );
}

export interface BroadcastGameRow {
  game_id: number;
  scheduled_date: string;
  away_team_id: number;
  home_team_id: number;
  away_runs: number;
  home_runs: number;
  innings: number;
}

type TeamPanel = {
  name: string;
  abbr: string;
  logo?: string;
  color_primary?: string;
  color_secondary?: string;
  color_text?: string;
};

export type PlayByPlayParsedRow = {
  text: string;
  inning: number;
  half: string;
  outs: number;
  away_score: number;
  home_score: number;
  event_type: string;
  isInningIntro: boolean;
  runner_on_1b: string | null;
  runner_on_2b: string | null;
  runner_on_3b: string | null;
};

export type BroadcastPlaybackSpeed = number;

export interface BroadcastGameProps {
  isActive: boolean;
  isLoading: boolean;
  isPaused: boolean;
  /** Playback multiplier used by delay timing (higher = faster). */
  playbackSpeed: BroadcastPlaybackSpeed;
  /** Increment to restart playback from the first play */
  replayNonce: number;
  onPlaybackComplete?: () => void;
  /** When true, center scoreboard (bases / inning / outs) shows a large FINAL SCORE label */
  playbackComplete?: boolean;
  /** Called when rewinding so parent can clear “playback finished” UI */
  onRewind?: () => void;
  /** When true, suppress popup overlays and show feed rows only. */
  feedOnly?: boolean;
  /** When true, render all feed rows immediately (no timed playback). */
  showAllRows?: boolean;
  /** Recap-specific presentation tweaks (labels/date/icons/name display). */
  isRecapView?: boolean;
  /** Date shown above FINAL SCORE in recap view. */
  recapDateLabel?: string;
  playByPlayRows: Record<string, unknown>[];
  announcementKey: string;
  gameRow: BroadcastGameRow;
  awayTeam: TeamPanel;
  homeTeam: TeamPanel;
}

function parseBool(v: unknown): boolean {
  if (v === true || v === 'true') return true;
  return false;
}

function parseRunner(v: unknown): string | null {
  if (!v || v === 'false') return null;
  if (v === true || v === 'true') return 'Runner';
  return String(v);
}

/** PBP row score from API — only this column on the row (away_score / home_score per play). */
function parseLineScore(row: Record<string, unknown>, key: 'away_score' | 'home_score'): number {
  const raw = row[key];
  if (raw === null || raw === undefined || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function parsePbpRows(
  rows: Record<string, unknown>[],
  announcementKey: string
): PlayByPlayParsedRow[] {
  return rows.map((row) => {
    const text = String(row[announcementKey] ?? row.play_text ?? '');
    const event_type = String(row.event_type ?? '');
    const isInningIntro =
      /it's the (top|bottom)/i.test(text) ||
      event_type.toLowerCase() === 'inning start' ||
      event_type.toLowerCase() === 'game start';
    return {
      text,
      inning: Number(row.inning) || 0,
      half: String(row.Half ?? row.half ?? 'top').toLowerCase(),
      outs: Math.min(3, Math.max(0, Number(row.outs) || 0)),
      away_score: parseLineScore(row, 'away_score'),
      home_score: parseLineScore(row, 'home_score'),
      event_type,
      isInningIntro,
      runner_on_1b: parseRunner(row.runner_on_1b),
      runner_on_2b: parseRunner(row.runner_on_2b),
      runner_on_3b: parseRunner(row.runner_on_3b),
    };
  });
}

function ordinal(n: number): string {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return `${n}st`;
  if (j === 2 && k !== 12) return `${n}nd`;
  if (j === 3 && k !== 13) return `${n}rd`;
  return `${n}th`;
}

/** First play index of the half-inning before the half containing `atIndex`. */
function findPreviousHalfStartIndex(parsed: PlayByPlayParsedRow[], atIndex: number): number | null {
  if (atIndex <= 0) return null;
  const row = parsed[atIndex];
  const inning = row.inning;
  const half = row.half;
  let targetInning: number;
  let targetHalf: string;
  if (half === 'bottom') {
    targetInning = inning;
    targetHalf = 'top';
  } else {
    targetInning = inning - 1;
    targetHalf = 'bottom';
  }
  if (targetInning < 1) return null;
  for (let i = 0; i < parsed.length; i++) {
    if (parsed[i].inning === targetInning && parsed[i].half === targetHalf) {
      return i;
    }
  }
  return null;
}

export type BroadcastGameHandle = {
  rewindToPreviousHalf: () => void;
};

/** Fade-in when popup mounts; fade-out uses CSS transition on the same box. */
const popupKeyframes = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const BROADCAST_PBP_POPUP_FADE_OUT_MS = 400;

const committedKeyframes = keyframes`
  from { opacity: 0.35; }
  to { opacity: 1; }
`;

const IDLE_SCOREBOARD: Pick<
  PlayByPlayParsedRow,
  'away_score' | 'home_score' | 'inning' | 'half' | 'outs' | 'runner_on_1b' | 'runner_on_2b' | 'runner_on_3b'
> = {
  away_score: 0,
  home_score: 0,
  inning: 1,
  half: 'top',
  outs: 0,
  runner_on_1b: null,
  runner_on_2b: null,
  runner_on_3b: null,
};

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Bases: 2nd top, 1st right, 3rd left; home plate bottom — gray outline only (not a baserunner slot).
 */
function BasesDiamond({
  firstRunner,
  secondRunner,
  thirdRunner,
}: {
  firstRunner: string | null;
  secondRunner: string | null;
  thirdRunner: string | null;
}) {
  const line = '2px solid';
  const teal = 'teal.500';
  const tealFill = 'rgba(45, 212, 191, 0.45)';
  const gray = 'gray.600';
  const sz = '14px';
  const baseStyle = (filled: boolean) => ({
    border: line,
    borderColor: teal,
    bg: filled ? tealFill : 'transparent',
  });
  return (
    <Box position="relative" w="52px" h="52px" flexShrink={0}>
      <Tooltip label={secondRunner} isDisabled={!secondRunner} hasArrow placement="top">
        <Box
          position="absolute"
          top="2px"
          left="50%"
          transform="translateX(-50%) rotate(45deg)"
          w={sz}
          h={sz}
          {...baseStyle(!!secondRunner)}
          cursor={secondRunner ? 'help' : 'default'}
        />
      </Tooltip>
      <Tooltip label={firstRunner} isDisabled={!firstRunner} hasArrow placement="right">
        <Box
          position="absolute"
          top="50%"
          right="2px"
          transform="translateY(-50%) rotate(45deg)"
          w={sz}
          h={sz}
          {...baseStyle(!!firstRunner)}
          cursor={firstRunner ? 'help' : 'default'}
        />
      </Tooltip>
      <Tooltip label={thirdRunner} isDisabled={!thirdRunner} hasArrow placement="left">
        <Box
          position="absolute"
          top="50%"
          left="2px"
          transform="translateY(-50%) rotate(45deg)"
          w={sz}
          h={sz}
          {...baseStyle(!!thirdRunner)}
          cursor={thirdRunner ? 'help' : 'default'}
        />
      </Tooltip>
      <Box
        position="absolute"
        bottom="2px"
        left="50%"
        transform="translateX(-50%) rotate(45deg)"
        w={sz}
        h={sz}
        border={line}
        borderColor={gray}
        bg="transparent"
      />
    </Box>
  );
}

function OutsDots({ outs }: { outs: number }) {
  return (
    <HStack spacing={1}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          w="10px"
          h="10px"
          borderRadius="full"
          borderWidth="2px"
          borderColor="teal.500"
          bg={i < outs ? 'teal.500' : 'transparent'}
        />
      ))}
    </HStack>
  );
}

const BroadcastGame = forwardRef<BroadcastGameHandle, BroadcastGameProps>(function BroadcastGame(
  {
    isActive,
    isLoading,
    isPaused,
    playbackSpeed,
    replayNonce,
    onPlaybackComplete,
    playbackComplete = false,
    onRewind,
    feedOnly = false,
    showAllRows = false,
    isRecapView = false,
    recapDateLabel,
    playByPlayRows,
    announcementKey,
    gameRow,
    awayTeam,
    homeTeam,
  },
  ref
) {
  const parsed = useMemo(
    () => parsePbpRows(playByPlayRows, announcementKey),
    [playByPlayRows, announcementKey]
  );
  const parsedRef = useRef(parsed);
  parsedRef.current = parsed;

  const endRef = useRef<HTMLDivElement>(null);
  const isPausedRef = useRef(isPaused);
  const playbackSpeedRef = useRef(playbackSpeed);
  const feedOnlyRef = useRef(feedOnly);
  const onPlaybackCompleteRef = useRef(onPlaybackComplete);
  const liveIndexRef = useRef(-1);
  const committedLenRef = useRef(0);
  const playbackCompleteRef = useRef(playbackComplete);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);
  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);
  useEffect(() => {
    feedOnlyRef.current = feedOnly;
    if (feedOnly) setPopup(null);
  }, [feedOnly]);
  useEffect(() => {
    onPlaybackCompleteRef.current = onPlaybackComplete;
  }, [onPlaybackComplete]);
  useEffect(() => {
    playbackCompleteRef.current = playbackComplete;
  }, [playbackComplete]);

  const [playbackStartIndex, setPlaybackStartIndex] = useState(0);
  const [runVersion, setRunVersion] = useState(0);

  const [committed, setCommitted] = useState<PlayByPlayParsedRow[]>([]);
  const [popup, setPopup] = useState<{ row: PlayByPlayParsedRow; fadingOut: boolean } | null>(null);
  const [liveIndex, setLiveIndex] = useState<number>(-1);

  useEffect(() => {
    liveIndexRef.current = liveIndex;
  }, [liveIndex]);
  useEffect(() => {
    committedLenRef.current = committed.length;
  }, [committed.length]);

  useImperativeHandle(
    ref,
    () => ({
      rewindToPreviousHalf: () => {
        const list = parsedRef.current;
        if (!list.length) return;
        let at: number;
        if (playbackCompleteRef.current) at = list.length - 1;
        else if (liveIndexRef.current >= 0) at = liveIndexRef.current;
        else at = Math.max(0, committedLenRef.current - 1);
        const nextStart = findPreviousHalfStartIndex(list, at);
        if (nextStart === null) return;
        onRewind?.();
        setPlaybackStartIndex(nextStart);
        setRunVersion((v) => v + 1);
      },
    }),
    [onRewind]
  );

  const resetPlayback = useCallback(() => {
    setCommitted([]);
    setPopup(null);
    setLiveIndex(-1);
  }, []);

  useEffect(() => {
    if (!showAllRows) return;
    setPopup(null);
    setCommitted(parsed);
    setLiveIndex(parsed.length > 0 ? parsed.length - 1 : -1);
  }, [showAllRows, parsed]);

  useEffect(() => {
    if (showAllRows) return;
    if (!isActive || isLoading || parsed.length === 0) {
      resetPlayback();
      return;
    }

    let cancelled = false;
    const start = playbackStartIndex;

    async function waitWhilePaused() {
      while (isPausedRef.current && !cancelled) {
        await delay(100);
      }
    }

    async function delayOrPause(baseMs: number) {
      const speed = Math.max(0.1, playbackSpeedRef.current);
      const totalMs = Math.round(baseMs / speed);
      const chunk = 100;
      let elapsed = 0;
      while (elapsed < totalMs && !cancelled) {
        await waitWhilePaused();
        if (cancelled) return;
        const step = Math.min(chunk, totalMs - elapsed);
        await delay(step);
        elapsed += step;
      }
    }

    (async () => {
      const initialCommitted = start > 0 ? parsed.slice(0, start) : [];
      setCommitted(initialCommitted);
      setPopup(null);
      setLiveIndex(-1);
      await delay(0);

      for (let i = start; i < parsed.length; i++) {
        if (cancelled) return;
        const row = parsed[i];
        setLiveIndex(i);
        const popupHoldMs = row.isInningIntro
          ? Math.round(BROADCAST_PBP_POPUP_MS * BROADCAST_PBP_INNING_POPUP_MULTIPLIER)
          : BROADCAST_PBP_POPUP_MS;
        if (!feedOnlyRef.current) {
          setPopup({ row, fadingOut: false });
          await delayOrPause(popupHoldMs);
          if (cancelled) return;
          if (!feedOnlyRef.current) {
            setPopup({ row, fadingOut: true });
            await delayOrPause(BROADCAST_PBP_POPUP_FADE_OUT_MS);
            if (cancelled) return;
            setPopup(null);
          } else {
            setPopup(null);
          }
        } else {
          await delayOrPause(popupHoldMs);
          if (cancelled) return;
        }
        setCommitted((c) => [...c, row]);
        await delayOrPause(BROADCAST_PBP_BETWEEN_MS);
      }
      if (!cancelled) {
        setLiveIndex(parsed.length - 1);
        onPlaybackCompleteRef.current?.();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isActive, isLoading, parsed, gameRow.game_id, replayNonce, resetPlayback, playbackStartIndex, runVersion, showAllRows]);

  useLayoutEffect(() => {
    if (showAllRows) return;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [committed.length, showAllRows]);

  const activeRow =
    popup?.row ?? (liveIndex >= 0 && parsed[liveIndex] ? parsed[liveIndex] : null);
  const displayScores = activeRow ?? IDLE_SCOREBOARD;

  const awayBatting = displayScores.half === 'top';
  const inningLabel = `${displayScores.half === 'top' ? 'Top' : 'Bottom'} of the ${ordinal(
    displayScores.inning || 1
  )} Inning`;
  const sponsorLead = isRecapView ? 'Game Recap is brought to you by' : 'This inning brought to you by';

  const bg = '#0c0f14';
  const panelBg = '#12171f';
  const borderSubtle = 'rgba(255,255,255,0.08)';
  const horizontalGutterPx = 0;

  // bases are now read directly from displayScores for BasesDiamond tooltips

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px" bg={bg} borderRadius="md">
        <Spinner size="lg" color="teal.400" />
      </Flex>
    );
  }

  if (parsed.length === 0) {
    return (
      <Text color="gray.500" fontSize="sm" bg={bg} p={4} borderRadius="md">
        No play-by-play data.
      </Text>
    );
  }

  return (
    <Flex direction="column" flex={1} minH={0} bg={bg} borderRadius="md" overflow="hidden">
      {/* Sponsor / inning strip — left: inning; right half: “Brought to you by” above sponsor lines */}
      <Box px={horizontalGutterPx} py={3} borderBottom="1px solid" borderColor={borderSubtle} flexShrink={0}>
        <Grid
          templateColumns={{ base: '1fr', md: '1fr 1fr' }}
          gap={{ base: 3, md: 4 }}
          alignItems="start"
          w="100%"
        >
          <GridItem minW={0}>
            <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color="white" lineHeight="short">
              {isRecapView ? 'Game Recap' : inningLabel}
            </Text>
          </GridItem>
          <GridItem minW={0}>
            <Box
              borderRadius="lg"
              px={2}
              py={3}
              borderWidth="1px"
              borderColor="rgba(255,255,255,0.12)"
              bg="rgba(255,255,255,0.05)"
              backdropFilter="blur(14px)"
              boxShadow="0 4px 24px rgba(0,0,0,0.25)"
              sx={{ WebkitBackdropFilter: 'blur(14px)' }}
            >
              <Text fontSize="sm" color="gray.500" lineHeight="short" mb={1}>
                {sponsorLead}
              </Text>
              <Text fontSize="sm" color="teal.300" fontWeight="semibold" letterSpacing="tight">
                {SPONSOR_PRIMARY}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={0.5} lineHeight="short">
                {SPONSOR_TAGLINE}
              </Text>
            </Box>
          </GridItem>
        </Grid>
      </Box>

      {/* Scoreboard */}
      <Grid
        templateColumns={{ base: 'minmax(0,1fr) minmax(0,1fr)', md: 'minmax(0,1fr) auto minmax(0,1fr)' }}
        templateAreas={{
          base: `"away home" "center center"`,
          md: `"away center home"`,
        }}
        gap={3}
        px={horizontalGutterPx}
        py={3}
        borderBottom="1px solid"
        borderColor={borderSubtle}
        alignItems="center"
        flexShrink={0}
        bg={panelBg}
      >
        <GridItem minW={0} gridArea="away">
          <HStack spacing={2} justify="flex-start" align="center" flexWrap="nowrap">
            <Box flexShrink={0}>
              <TeamLogo
                team={{
                  name: awayTeam.name,
                  logo: awayTeam.logo,
                  color_primary: awayTeam.color_primary,
                  color_secondary: awayTeam.color_secondary,
                  color_text: awayTeam.color_text,
                }}
                size="sm"
              />
            </Box>
            <Text fontWeight="bold" fontSize="sm" color="gray.200" isTruncated maxW={{ base: '120px', md: 'full' }}>
              {isRecapView ? (
                <>
                  <Text as="span" display={{ base: 'inline', md: 'none' }}>
                    {awayTeam.abbr}
                  </Text>
                  <Text as="span" display={{ base: 'none', md: 'inline' }}>
                    {awayTeam.name}
                  </Text>
                </>
              ) : (
                awayTeam.abbr
              )}
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="white" flexShrink={0}>
              {displayScores.away_score}
            </Text>
            {!isRecapView && awayBatting && (
              <Icon as={ChevronLeftIcon} color="yellow.400" boxSize={5} flexShrink={0} aria-label="Away batting" />
            )}
          </HStack>
        </GridItem>

        <GridItem justifySelf="center" gridArea="center">
          {playbackComplete ? (
            <Box textAlign="center">
              {isRecapView && recapDateLabel ? (
                <Text fontSize="sm" color="gray.300" lineHeight="short" mb={1}>
                  {recapDateLabel}
                </Text>
              ) : null}
              <Text
                fontSize={{ base: 'xl', md: '3xl' }}
                fontWeight="extrabold"
                color="teal.200"
                letterSpacing="0.12em"
                textAlign="center"
                whiteSpace="nowrap"
                userSelect="none"
              >
                FINAL SCORE
              </Text>
            </Box>
          ) : (
            <Flex align="center" gap={3} flexShrink={0}>
              <Flex align="center" gap={1}>
                <Icon
                  as={ChevronUpIcon}
                  color="teal.400"
                  transform={displayScores.half === 'bottom' ? 'rotate(180deg)' : undefined}
                  boxSize={4}
                  aria-label={displayScores.half === 'top' ? 'Top' : 'Bottom'}
                />
                <Text fontWeight="bold" fontSize="lg" color="white">
                  {displayScores.inning || 1}
                </Text>
              </Flex>
              <BasesDiamond
                firstRunner={displayScores.runner_on_1b}
                secondRunner={displayScores.runner_on_2b}
                thirdRunner={displayScores.runner_on_3b}
              />
              <OutsDots outs={displayScores.outs} />
            </Flex>
          )}
        </GridItem>

        <GridItem minW={0} gridArea="home">
          <HStack spacing={2} justify="flex-end" align="center" flexWrap="nowrap">
            {!isRecapView && !awayBatting && (
              <Icon as={ChevronRightIcon} color="yellow.400" boxSize={5} flexShrink={0} aria-label="Home batting" />
            )}
            <Text fontSize="2xl" fontWeight="bold" color="white" flexShrink={0}>
              {displayScores.home_score}
            </Text>
            <Text fontWeight="bold" fontSize="sm" color="gray.200" isTruncated maxW={{ base: '120px', md: 'full' }}>
              {isRecapView ? (
                <>
                  <Text as="span" display={{ base: 'inline', md: 'none' }}>
                    {homeTeam.abbr}
                  </Text>
                  <Text as="span" display={{ base: 'none', md: 'inline' }}>
                    {homeTeam.name}
                  </Text>
                </>
              ) : (
                homeTeam.abbr
              )}
            </Text>
            <Box flexShrink={0}>
              <TeamLogo
                team={{
                  name: homeTeam.name,
                  logo: homeTeam.logo,
                  color_primary: homeTeam.color_primary,
                  color_secondary: homeTeam.color_secondary,
                  color_text: homeTeam.color_text,
                }}
                size="sm"
              />
            </Box>
          </HStack>
        </GridItem>
      </Grid>

      {/* Play-by-play: list scrolls independently; popup is a centered overlay (no layout reflow). */}
      <Box flex={1} minH={0} position="relative" display="flex" flexDirection="column">
        <Box flex={1} minH={0} overflowY="auto" px={horizontalGutterPx} py={3}>
          {committed.map((row, idx) => (
            <Box
              key={`${idx}-${row.text.slice(0, 40)}`}
              py={2}
              borderBottom="1px solid"
              borderColor={borderSubtle}
              animation={`${committedKeyframes} 0.4s ease-out`}
            >
              {row.event_type.toLowerCase() === 'inning start' ? (
                <InningStartFeedCard row={row} awayTeam={awayTeam} homeTeam={homeTeam} />
              ) : (
                <BroadcastPbpDisplayText
                  raw={row.text}
                  eventType={row.event_type}
                  outs={row.outs}
                  previousOuts={idx > 0 ? committed[idx - 1].outs : 0}
                  half={row.half}
                  awayScore={row.away_score}
                  homeScore={row.home_score}
                  previousAwayScore={idx > 0 ? committed[idx - 1].away_score : 0}
                  previousHomeScore={idx > 0 ? committed[idx - 1].home_score : 0}
                  awayTeamName={awayTeam.name}
                  homeTeamName={homeTeam.name}
                  outCountPlacement="afterTitle"
                  fontSize="sm"
                  color="gray.300"
                  whiteSpace="pre-wrap"
                />
              )}
            </Box>
          ))}
          <Box ref={endRef} h="1px" w="full" />
        </Box>

        {popup && (
          <Box position="absolute" inset={0} px={horizontalGutterPx} pointerEvents="none" zIndex={2}>
            <Box
              position="absolute"
              left="50%"
              top="28%"
              transform="translate(-50%, -50%)"
              w="70%"
              maxW="42rem"
            >
              <Box
                px={{ base: 5, md: 8 }}
                py={{ base: 5, md: 7 }}
                borderRadius="lg"
                borderWidth="2px"
                borderColor={popup.row.isInningIntro ? 'purple.400' : 'teal.400'}
                bg={popup.row.isInningIntro ? undefined : 'teal.900'}
                bgGradient={popup.row.isInningIntro ? 'linear(to-br, #1a1033, #2d1b4e)' : undefined}
                boxShadow={
                  popup.row.isInningIntro
                    ? '0 0 32px rgba(159, 122, 234, 0.4)'
                    : '0 0 28px rgba(45, 212, 191, 0.3)'
                }
                opacity={popup.fadingOut ? 0 : 1}
                transform={popup.fadingOut ? 'translateY(10px) scale(0.98)' : undefined}
                transition={
                  popup.fadingOut ? 'opacity 0.4s ease-out, transform 0.4s ease-out' : undefined
                }
                animation={
                  popup.fadingOut ? undefined : `${popupKeyframes} 0.45s ease-out forwards`
                }
              >
                {popup.row.isInningIntro ? (
                  <InningStartPopupText
                    row={popup.row}
                    fontSize={{ base: 'lg', md: 'xl' }}
                    color="purple.100"
                    textAlign="center"
                    fontWeight="medium"
                    lineHeight="snug"
                  />
                ) : (
                  <BroadcastPbpDisplayText
                    raw={popup.row.text}
                    eventType={popup.row.event_type}
                    outs={popup.row.outs}
                    previousOuts={
                      liveIndex > 0 && parsed[liveIndex - 1] ? parsed[liveIndex - 1].outs : 0
                    }
                    half={popup.row.half}
                    awayScore={popup.row.away_score}
                    homeScore={popup.row.home_score}
                    previousAwayScore={
                      liveIndex > 0 && parsed[liveIndex - 1] ? parsed[liveIndex - 1].away_score : 0
                    }
                    previousHomeScore={
                      liveIndex > 0 && parsed[liveIndex - 1] ? parsed[liveIndex - 1].home_score : 0
                    }
                    awayTeamName={awayTeam.name}
                    homeTeamName={homeTeam.name}
                    fontSize={{ base: 'lg', md: 'xl' }}
                    color="teal.50"
                    textAlign="center"
                    fontWeight="medium"
                    lineHeight="snug"
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Flex>
  );
});

export default BroadcastGame;
